import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Search, 
  Tag, 
  Calendar, 
  Clock, 
  Eye, 
  ChevronRight,
  Star,
  Newspaper,
  TrendingUp,
  Bookmark,
  Share2,
  ArrowRight,
  Play,
  Volume2,
  VolumeX,
  ChevronDown,
  Zap,
  Sparkles,
  ListIcon,
  GridIcon
} from 'lucide-react';
import NewsSidebar from '../../components/news/NewsSidebar';
import { Link } from 'react-router-dom';
import { useNews } from '../../hooks/useNews';
import { useNewsCategories } from '../../hooks/useNewsCategories';
import type { NewsArticleWithRelations, NewsCategory } from '../../types/news';
import { useNewsHandlers } from './components/NewsHandlers';
import { NewsHeader, BreakingNewsSection, NewsArticleGrid } from './components/NewsLayout';
import NewsHero from './components/NewsHero';
import TrendingArticles from './components/TrendingArticles';

const News = () => {
  const [articles, setArticles] = useState<NewsArticleWithRelations[]>([]);
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [videoMuted, setVideoMuted] = useState(true);
  const [hoveredArticle, setHoveredArticle] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isGridView, setIsGridView] = useState(false);
  
  // Scroll animations
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, -150]);
  const y2 = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0.2]);
  const scale = useTransform(scrollY, [0, 300], [1, 0.95]);
  const heroHeight = useTransform(scrollY, [0, 300], ['100vh', '85vh']);
  const titleScale = useTransform(scrollY, [0, 200], [1, 0.8]);
  
  // Mouse parallax effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothMouseX = useSpring(mouseX, { stiffness: 300, damping: 30 });
  const smoothMouseY = useSpring(mouseY, { stiffness: 300, damping: 30 });
  
  const handlers = useNewsHandlers();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { fetchArticles } = useNews();
  const { fetchCategories } = useNewsCategories();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    loadData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredArticles.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [articles]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [articlesData, categoriesData] = await Promise.all([
        fetchArticles(),
        fetchCategories()
      ]);
      setArticles(articlesData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const featuredArticles = articles.filter(article => article.is_featured);
  const breakingNews = articles.filter(article => article.is_breaking);
  const latestArticles = [...articles]
    .sort((a, b) => new Date(b.published_at || '').getTime() - new Date(a.published_at || '').getTime())
    .slice(0, 6);

  const filteredArticles = articles.filter(article => {
    const matchesCategory = !selectedCategory || article.category?.slug === selectedCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.content.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleShare = (article: NewsArticleWithRelations) => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt || '',
        url: window.location.href
      });
    }
  };

  const handleVideoToggle = () => {
    if (videoRef.current) {
      setVideoMuted(!videoMuted);
      videoRef.current.muted = !videoMuted;
    }
  };

  const scrollToContent = () => {
    const contentSection = document.getElementById('news-content');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="loader">Loading...</div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pt-16 bg-gradient-to-b from-background via-background/95 to-background/90 relative overflow-hidden"
      onMouseMove={(e) => handlers.handleMouseMove(e, mouseX, mouseY)}
    >
      {/* Dynamic Background Pattern */}
      <div className="absolute inset-0 w-full h-full">
        <div className="absolute inset-0 bg-grid-white/5 bg-[size:50px_50px] [mask-image:radial-gradient(white,transparent_85%)] pointer-events-none" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[60rem] h-[60rem] bg-primary/5 rounded-full blur-3xl animate-pulse" />
        </div>
      </div>

      {/* Header Section with Category Filters and Search */}
      <NewsHeader 
        categories={categories}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isGridView={isGridView}
        setIsGridView={setIsGridView}
      />

      <div className="relative max-w-7xl mx-auto px-4">
        {/* Breaking News Ticker */}
        <BreakingNewsSection breakingNews={breakingNews} />

        {/* Hero Section with Featured Article */}
        <NewsHero featuredArticles={featuredArticles} currentSlide={currentSlide} />

        {/* Trending Articles */}
        <TrendingArticles articles={latestArticles} />

        {/* Latest Articles List */}
        <div className="mb-16">
          <motion.div 
            className="flex items-center gap-3 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Newspaper className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold">Últimas Notícias</h2>
            <button onClick={() => { setIsGridView(!isGridView); loadData(); }} className="ml-4 px-4 py-2 bg-gradient-to-r from-app-primary to-app-secondary text-white rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-110 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-app-primary focus:ring-opacity-50" title={isGridView ? 'Switch to List View' : 'Switch to Grid View'}>
              {isGridView ? <ListIcon className="inline-block mr-2" /> : <GridIcon className="inline-block mr-2" />}
              {isGridView ? 'Vista em Lista' : 'Vista em Grade'}
            </button>
          </motion.div>
          
          {isGridView ? (
            <NewsArticleGrid articles={filteredArticles} />
          ) : (
            <div className="space-y-6">
              {filteredArticles.map((article) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="group relative bg-gradient-to-br from-card/30 via-card/20 to-card/10 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/20 transition-all duration-500 transform hover:-translate-y-1"
                >
                  <Link to={`/news/${article.slug}`} className="block p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      {article.cover_image && (
                        <div className="w-full md:w-48 h-48 shrink-0 rounded-xl overflow-hidden shadow-lg group-hover:shadow-xl transition-shadow duration-500">
                          <img
                            src={article.cover_image}
                            alt={article.title}
                            className="w-full h-full object-cover transition-transform group-hover:scale-110"
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-4">
                          <span className="px-4 py-1.5 bg-gradient-to-r from-secondary/50 to-secondary/30 rounded-full text-sm font-medium shadow-inner group-hover:shadow-secondary/20 transition-all duration-500">
                            {article.category?.name}
                          </span>
                          <span className="text-sm text-gray-400 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {new Date(article.published_at || '').toLocaleDateString()}
                          </span>
                        </div>
                        <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent group-hover:from-primary group-hover:to-blue-400 transition-all duration-500">{article.title}</h3>
                        <p className="text-gray-300 mb-4 line-clamp-2 group-hover:text-gray-100 transition-colors duration-500 leading-relaxed">{article.excerpt}</p>
                        <div className="flex items-center justify-between text-sm text-gray-400">
                          <span className="flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            {article.views_count} visualizações
                          </span>
                          <span className="text-primary group-hover:translate-x-2 transition-all duration-500 flex items-center gap-1 font-medium">
                            Ler mais <ChevronRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {filteredArticles.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">No articles found matching your criteria.</p>
            <button
              onClick={() => { setSelectedCategory(null); setSearchQuery(''); }}
              className="mt-4 text-primary hover:text-primary/80 transition-colors"
            >
              Clear filters and show all articles
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default News;